export type LabeledValue = { value: string; label: string };

/** The seven choices on the website contact form, in page order. */
export const VOGELKOP_SERVICES: LabeledValue[] = [
  {
    value: "site-feasibility-zoning-review",
    label: "Site Feasibility & Zoning Review",
  },
  {
    value: "concept-design-preliminary-plans",
    label: "Concept Design & Preliminary Plans",
  },
  { value: "architectural-drawings", label: "Architectural Drawings" },
  {
    value: "permitting-zoning-support",
    label: "Permitting & Zoning Support",
  },
  {
    value: "custom-home-design-build",
    label: "Custom Home Design & Build",
  },
  { value: "renovation-addition", label: "Renovation / Addition" },
  { value: "not-sure", label: "Not Sure — I Need Guidance" },
];

export const VOGELKOP_STAGES: LabeledValue[] = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "appointment-set", label: "Appointment set" },
  { value: "negotiation", label: "Negotiation" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" },
];

/** Starting list. Settings can edit it. Mariia can replace these. */
export const VOGELKOP_LOST_REASONS: LabeledValue[] = [
  { value: "budget", label: "Budget" },
  { value: "timing", label: "Timing" },
  { value: "chose-someone-else", label: "Chose someone else" },
  { value: "not-a-fit", label: "Not a fit" },
  { value: "no-response", label: "No response" },
  { value: "other", label: "Other" },
];

export const VOGELKOP_BUDGETS = [
  "$500K–$999K",
  "$1M–$1.99M",
  "$2M–$3.99M",
  "$4M+",
  "Still determining the scope",
] as const;

export const STUDIO_EMAIL = "studio@vogelkop.dev";
export const STUDIO_FROM = "Vogelkop <studio@vogelkop.dev>";

export const WEBSITE_CONTACT_SOURCE = "Website contact form";
export const GUIDE_FORM_SOURCE = "Guide form";

export const VOGELKOP_SOURCE_NAMES = [
  WEBSITE_CONTACT_SOURCE,
  GUIDE_FORM_SOURCE,
  "Added by hand",
  "Spreadsheet import",
  "Text message",
  "Facebook lead ad",
  "Instagram lead ad",
] as const;

export function serviceValueFromInput(
  raw: string | null | undefined,
  services: LabeledValue[] = VOGELKOP_SERVICES,
): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const byValue = services.find((item) => item.value === trimmed);
  if (byValue) return byValue.value;
  const lower = trimmed.toLowerCase();
  return (
    services.find((item) => item.label.toLowerCase() === lower)?.value ?? null
  );
}

export function canonicalSourceName(source: string | null | undefined): string {
  const value = (source ?? "").trim().toLowerCase().replace(/\.$/, "");
  if (
    value === "guide form" ||
    value === "guide" ||
    value === "field guide" ||
    value === "land guide"
  ) {
    return GUIDE_FORM_SOURCE;
  }
  if (
    value === "" ||
    value === "website contact form" ||
    value === "contact" ||
    value === "website" ||
    value === "contact form"
  ) {
    return WEBSITE_CONTACT_SOURCE;
  }
  return (source ?? "").trim();
}

export function normalizeEmail(email: string | null | undefined): string {
  return (email ?? "").trim().toLowerCase();
}

export function normalizePhone(phone: string | null | undefined): string {
  return (phone ?? "").replace(/\D/g, "");
}

export function phonesMatch(
  left: string | null | undefined,
  right: string | null | undefined,
): boolean {
  const a = normalizePhone(left);
  const b = normalizePhone(right);
  if (a.length < 7 || b.length < 7) return false;
  const tail = (value: string) =>
    value.length > 10 ? value.slice(-10) : value;
  return tail(a) === tail(b);
}

export function splitPersonName(name: string | null | undefined): {
  first_name: string;
  last_name: string;
} {
  const trimmed = (name ?? "").trim().replace(/\s+/g, " ");
  if (!trimmed) return { first_name: "New lead", last_name: "" };
  const space = trimmed.indexOf(" ");
  if (space === -1) return { first_name: trimmed, last_name: "" };
  return {
    first_name: trimmed.slice(0, space),
    last_name: trimmed.slice(space + 1),
  };
}

export type Assignee = { id: number; disabled?: boolean | null };

/** Counts of open leads keyed by sales id. Used only when rotation is on. */
export function pickRoundRobinAssignee(
  sales: Assignee[],
  openLeadCounts: Record<string, number>,
  enabled: boolean,
): number | null {
  if (!enabled) return null;
  const active = sales.filter((sale) => !sale.disabled);
  if (active.length === 0) return null;
  const ranked = [...active].sort((a, b) => {
    const countA = openLeadCounts[String(a.id)] ?? 0;
    const countB = openLeadCounts[String(b.id)] ?? 0;
    if (countA !== countB) return countA - countB;
    return a.id - b.id;
  });
  return ranked[0]?.id ?? null;
}

export function secretsMatch(
  provided: string | null,
  expected: string | null | undefined,
): boolean {
  if (!provided || !expected) return false;
  if (provided.length !== expected.length) return false;
  let mismatch = 0;
  for (let i = 0; i < provided.length; i += 1) {
    mismatch |= provided.charCodeAt(i) ^ expected.charCodeAt(i);
  }
  return mismatch === 0;
}
